/**
 * ActivityTrendChart - Activity trends over time with period selection and export
 * 
 * Features:
 * - Three-line chart (Active/Inactive/Zombie)
 * - Time period selector (7d, 30d, 90d, custom)
 * - Hover tooltips with date and counts
 * - Export to PNG and CSV
 * - Responsive design
 * - Keyboard navigation and screen reader support
 */

import React, { useState, useRef } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import './ActivityTrendChart.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Activity status colors (matching site style guide)
const ACTIVITY_COLORS = {
  active: {
    line: '#10B981',      // Success green
    background: 'rgba(16, 185, 129, 0.1)'
  },
  inactive: {
    line: '#F59E0B',      // Warning yellow
    background: 'rgba(245, 158, 11, 0.1)'
  },
  zombie: {
    line: '#EF4444',      // Error red
    background: 'rgba(239, 68, 68, 0.1)'
  }
};

const PERIOD_OPTIONS = [
  { value: '7d', label: '7 Days' },
  { value: '30d', label: '30 Days' },
  { value: '90d', label: '90 Days' },
  { value: 'custom', label: 'Custom Range' }
];

const ActivityTrendChart = ({
  data,
  period = '30d',
  onPeriodChange,
  loading = false,
  error = null,
  height = 400,
  className = ''
}) => {
  const chartRef = useRef(null);
  const [showCustomRange, setShowCustomRange] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  // Handle period change
  const handlePeriodChange = (newPeriod) => {
    if (newPeriod === 'custom') {
      setShowCustomRange(true);
    } else {
      setShowCustomRange(false);
      if (onPeriodChange) {
        onPeriodChange(newPeriod);
      }
    }
  };

  // Handle custom date range submission
  const handleCustomRangeSubmit = () => {
    if (customStartDate && customEndDate && onPeriodChange) {
      onPeriodChange('custom', { startDate: customStartDate, endDate: customEndDate });
      setShowCustomRange(false);
    }
  };

  // Export chart as PNG
  const exportToPNG = () => {
    if (chartRef.current) {
      const chart = chartRef.current;
      const url = chart.toBase64Image();
      const link = document.createElement('a');
      link.download = `activity-trends-${period}-${new Date().toISOString().split('T')[0]}.png`;
      link.href = url;
      link.click();
    }
  };

  // Export data as CSV
  const exportToCSV = () => {
    if (!data || !data.dates) return;

    // Create CSV header
    const headers = ['Date', 'Active', 'Inactive', 'Zombie', 'Total'];
    
    // Create CSV rows
    const rows = data.dates.map((date, index) => {
      return [
        date,
        data.active[index] || 0,
        data.inactive[index] || 0,
        data.zombie[index] || 0,
        data.total ? data.total[index] || 0 : (data.active[index] || 0) + (data.inactive[index] || 0) + (data.zombie[index] || 0)
      ].join(',');
    });

    // Combine header and rows
    const csv = [headers.join(','), ...rows].join('\n');

    // Create download link
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.download = `activity-trends-${period}-${new Date().toISOString().split('T')[0]}.csv`;
    link.href = url;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Loading state
  if (loading) {
    return (
      <div className={`activity-trend-chart activity-trend-chart--loading ${className}`}>
        <div className="activity-trend-chart__header">
          <h3 className="activity-trend-chart__title">Activity Trends Over Time</h3>
        </div>
        <div className="activity-trend-chart__loading" style={{ height }}>
          <div className="admin-spinner"></div>
          <p>Loading trend data...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={`activity-trend-chart activity-trend-chart--error ${className}`}>
        <div className="activity-trend-chart__header">
          <h3 className="activity-trend-chart__title">Activity Trends Over Time</h3>
        </div>
        <div className="activity-trend-chart__error" style={{ height }}>
          <p className="activity-trend-chart__error-message">{error}</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (!data || !data.dates || data.dates.length === 0) {
    return (
      <div className={`activity-trend-chart activity-trend-chart--empty ${className}`}>
        <div className="activity-trend-chart__header">
          <h3 className="activity-trend-chart__title">Activity Trends Over Time</h3>
        </div>
        <div className="activity-trend-chart__empty" style={{ height }}>
          <p>No trend data available for the selected period.</p>
        </div>
      </div>
    );
  }

  // Prepare chart data
  const chartData = {
    labels: data.dates,
    datasets: [
      {
        label: 'Active',
        data: data.active,
        borderColor: ACTIVITY_COLORS.active.line,
        backgroundColor: ACTIVITY_COLORS.active.background,
        tension: 0.4,
        fill: true,
        pointBackgroundColor: ACTIVITY_COLORS.active.line,
        pointBorderColor: '#FFFFFF',
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointHoverBackgroundColor: ACTIVITY_COLORS.active.line,
        pointHoverBorderColor: '#FFFFFF',
        pointHoverBorderWidth: 2
      },
      {
        label: 'Inactive',
        data: data.inactive,
        borderColor: ACTIVITY_COLORS.inactive.line,
        backgroundColor: ACTIVITY_COLORS.inactive.background,
        tension: 0.4,
        fill: true,
        pointBackgroundColor: ACTIVITY_COLORS.inactive.line,
        pointBorderColor: '#FFFFFF',
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointHoverBackgroundColor: ACTIVITY_COLORS.inactive.line,
        pointHoverBorderColor: '#FFFFFF',
        pointHoverBorderWidth: 2
      },
      {
        label: 'Zombie',
        data: data.zombie,
        borderColor: ACTIVITY_COLORS.zombie.line,
        backgroundColor: ACTIVITY_COLORS.zombie.background,
        tension: 0.4,
        fill: true,
        pointBackgroundColor: ACTIVITY_COLORS.zombie.line,
        pointBorderColor: '#FFFFFF',
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointHoverBackgroundColor: ACTIVITY_COLORS.zombie.line,
        pointHoverBorderColor: '#FFFFFF',
        pointHoverBorderWidth: 2
      }
    ]
  };

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        align: 'end',
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 12,
            family: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
          },
          generateLabels: (chart) => {
            const datasets = chart.data.datasets;
            return datasets.map((dataset, i) => ({
              text: dataset.label,
              fillStyle: dataset.borderColor,
              hidden: false,
              index: i,
              pointStyle: 'circle'
            }));
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 13,
          weight: '600'
        },
        bodyFont: {
          size: 12
        },
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        displayColors: true,
        usePointStyle: true,
        callbacks: {
          title: (context) => {
            return context[0].label;
          },
          label: (context) => {
            const label = context.dataset.label || '';
            const value = context.parsed.y;
            return `${label}: ${value} users`;
          },
          footer: (context) => {
            // Calculate total for this date
            const total = context.reduce((sum, item) => sum + item.parsed.y, 0);
            return `Total: ${total} users`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.05)'
        },
        ticks: {
          font: {
            size: 11
          },
          maxRotation: 45,
          minRotation: 0
        },
        title: {
          display: true,
          text: 'Date',
          font: {
            size: 12,
            weight: '600'
          }
        }
      },
      y: {
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.05)'
        },
        ticks: {
          font: {
            size: 11
          },
          precision: 0
        },
        beginAtZero: true,
        title: {
          display: true,
          text: 'User Count',
          font: {
            size: 12,
            weight: '600'
          }
        }
      }
    },
    interaction: {
      mode: 'index',
      intersect: false
    }
  };

  // Generate accessible description
  const generateAriaLabel = () => {
    if (!data || !data.dates || data.dates.length === 0) {
      return 'Empty activity trend chart';
    }
    
    const firstDate = data.dates[0];
    const lastDate = data.dates[data.dates.length - 1];
    const avgActive = Math.round(data.active.reduce((sum, val) => sum + val, 0) / data.active.length);
    const avgInactive = Math.round(data.inactive.reduce((sum, val) => sum + val, 0) / data.inactive.length);
    const avgZombie = Math.round(data.zombie.reduce((sum, val) => sum + val, 0) / data.zombie.length);
    
    return `Activity trends from ${firstDate} to ${lastDate}. Average active users: ${avgActive}, inactive: ${avgInactive}, zombie: ${avgZombie}.`;
  };

  return (
    <div className={`activity-trend-chart ${className}`}>
      {/* Header with controls */}
      <div className="activity-trend-chart__header">
        <h3 className="activity-trend-chart__title">Activity Trends Over Time</h3>
        
        <div className="activity-trend-chart__controls">
          {/* Period selector */}
          <div className="activity-trend-chart__period-selector">
            {PERIOD_OPTIONS.map((option) => (
              <button
                key={option.value}
                className={`activity-trend-chart__period-btn ${period === option.value ? 'activity-trend-chart__period-btn--active' : ''}`}
                onClick={() => handlePeriodChange(option.value)}
                aria-pressed={period === option.value}
              >
                {option.label}
              </button>
            ))}
          </div>

          {/* Export buttons */}
          <div className="activity-trend-chart__export-buttons">
            <button
              className="activity-trend-chart__export-btn"
              onClick={exportToPNG}
              title="Export as PNG image"
              aria-label="Export chart as PNG image"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M8 12L3 7h3V1h4v6h3l-5 5z"/>
                <path d="M1 14h14v2H1z"/>
              </svg>
              PNG
            </button>
            <button
              className="activity-trend-chart__export-btn"
              onClick={exportToCSV}
              title="Export as CSV data"
              aria-label="Export data as CSV file"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M8 12L3 7h3V1h4v6h3l-5 5z"/>
                <path d="M1 14h14v2H1z"/>
              </svg>
              CSV
            </button>
          </div>
        </div>
      </div>

      {/* Custom date range picker */}
      {showCustomRange && (
        <div className="activity-trend-chart__custom-range">
          <label>
            Start Date:
            <input
              type="date"
              value={customStartDate}
              onChange={(e) => setCustomStartDate(e.target.value)}
              className="activity-trend-chart__date-input"
            />
          </label>
          <label>
            End Date:
            <input
              type="date"
              value={customEndDate}
              onChange={(e) => setCustomEndDate(e.target.value)}
              className="activity-trend-chart__date-input"
            />
          </label>
          <button
            className="activity-trend-chart__apply-btn"
            onClick={handleCustomRangeSubmit}
            disabled={!customStartDate || !customEndDate}
          >
            Apply
          </button>
          <button
            className="activity-trend-chart__cancel-btn"
            onClick={() => setShowCustomRange(false)}
          >
            Cancel
          </button>
        </div>
      )}

      {/* Chart container */}
      <div 
        className="activity-trend-chart__chart-container"
        style={{ height }}
        role="img"
        aria-label={generateAriaLabel()}
        tabIndex={0}
      >
        <Line ref={chartRef} data={chartData} options={chartOptions} />
      </div>

      {/* Text alternative for screen readers */}
      <div className="admin-sr-only">
        <p>Activity trends chart showing user activity over time</p>
        <table>
          <caption>Activity data by date</caption>
          <thead>
            <tr>
              <th scope="col">Date</th>
              <th scope="col">Active</th>
              <th scope="col">Inactive</th>
              <th scope="col">Zombie</th>
            </tr>
          </thead>
          <tbody>
            {data.dates && data.dates.map((date, index) => (
              <tr key={index}>
                <td>{date}</td>
                <td>{data.active[index]}</td>
                <td>{data.inactive[index]}</td>
                <td>{data.zombie[index]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ActivityTrendChart;
